import { useEffect, useRef, useState } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import type { OwnerPlotDataResponse } from "../../types";
import { useOwnerPlot } from "@/features/map/hooks/useOwnerPlot";
import { toast } from "react-toastify";
import { formatArea } from "../../utils";
import { createRoot } from "react-dom/client";

interface OwnerPlotLayerProps {
  defaultVisible: boolean;
}

export const OWNER_PLOT_LAYER_NAME = "登記解析済み所有者名を表示する(土地)";

const MIN_ZOOM = 16;
const MAX_ZOOM = 20;

export const OwnerPlotLayer: React.FC<OwnerPlotLayerProps> = ({
  defaultVisible,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const [visible, setVisible] = useState(defaultVisible);
  const [zoomLevel, setZoomLevel] = useState<number>(map.getZoom());

  const { ownerPlotData, isLoading, isError } = useOwnerPlot();
  const toastDisplayed = useRef<boolean>(false);

  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(map.getZoom());
    };

    map.on("zoomend", handleZoomEnd);

    map.on("overlayadd", (e) => {
      if (e.name === OWNER_PLOT_LAYER_NAME) {
        setVisible(true);
      }
    });

    map.on("overlayremove", (e) => {
      if (e.name === OWNER_PLOT_LAYER_NAME) {
        setVisible(false);
      }
    });

    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !visible || !ownerPlotData) return;

    if (layerRef.current) {
      layerRef.current.clearLayers();
    } else {
      layerRef.current = L.layerGroup().addTo(map);
    }

    if (ownerPlotData.length > 0) {
      const markers = ownerPlotData.map((plot) => createOwnerPlotMarker(plot));
      markers.forEach((marker) => {
        layerRef.current?.addLayer(marker);
      });
    }
  }, [map, visible, ownerPlotData]);

  useEffect(() => {
    if (layerRef.current) {
      if (
        visible &&
        zoomLevel &&
        zoomLevel - 1 >= MIN_ZOOM &&
        zoomLevel - 1 <= MAX_ZOOM
      ) {
        layerRef.current.addTo(map);
      } else {
        map.removeLayer(layerRef.current);
      }
    }
  }, [zoomLevel, map, visible]);

  if (
    !isLoading &&
    ownerPlotData &&
    ownerPlotData.length > 0 &&
    !toastDisplayed.current
  ) {
    toast.success("所有者情報を地図に反映しました。");
    toastDisplayed.current = true;
  }
  if (isError) {
    toast.error("所有者情報の取得に失敗しました。");
  }

  return <FeatureGroup />;
};

/**
 * 所有者名の表示文言を生成する関数
 * @param ownerNames 所有者名の配列
 * @return 表示文言
 */
const getOwnerNamesDisplay = (ownerNames: string[]): string => {
  return ownerNames.length > 1
    ? `${ownerNames[0]}、その他${ownerNames.length - 1}人`
    : ownerNames[0];
};

/**
 * 所有者名のポップアップを生成する関数
 * @param plot 所有者データ
 * @return L.Popup
 */
const createOwnerPlotPopup = (plot: OwnerPlotDataResponse): L.Popup => {
  const ownerNamesDisplay = getOwnerNamesDisplay(plot.ownerNames);
  const popupHTML = document.createElement("div");

  createRoot(popupHTML).render(
    <div>
      <strong>所在:</strong> {plot.location}
      <br />
      <strong>地番:</strong> {plot.chiban}
      <br />
      <strong>推計地積:</strong> {formatArea(plot.area ?? null)}(㎡)
      <br />
      <strong>所有者名:</strong> {ownerNamesDisplay}
      <br />
      <strong>
        <a href={plot.filePath} target="_blank" rel="noopener noreferrer">
          所有者事項をPDFでダウンロード
        </a>
      </strong>
      <br />
    </div>
  );

  return L.popup({
    content: popupHTML,
  });
};

/**
 * 所有者名を含むマーカー生成
 * @param ownerNames 所有者名
 * @return L.DivIcon
 */
const createMarkerWithOwnerName = (
  ownerNames: OwnerPlotDataResponse["ownerNames"]
): L.DivIcon => {
  const ownerNamesDisplay = getOwnerNamesDisplay(ownerNames);

  return L.divIcon({
    className: "owner-plot-marker", // css上で特にスタイル指定していないが、classNameを追加しないとピンに白背景がつく
    html: `
      <div style="position: relative; text-align: center;">
        <img src="https://esm.sh/leaflet@1.9.4/dist/images/marker-icon.png"/>
        <div style="
          position: absolute; 
          top: 45px; 
          left: 50%; 
          transform: translateX(-50%); 
          white-space: nowrap;
          color: black;
          text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white;">
          ${ownerNamesDisplay}
        </div>
      </div>
    `,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

/**
 * 所有者プロットのマーカーを生成し、クリックイベントを追加する関数
 * @param plot 所有者データ
 * @return L.Marker
 */
const createOwnerPlotMarker = (plot: OwnerPlotDataResponse): L.Marker => {
  const ownerPlotPopup = createOwnerPlotPopup(plot);
  const customIcon = createMarkerWithOwnerName(plot.ownerNames);

  const marker = L.marker([plot.latitude, plot.longitude], {
    icon: customIcon,
  });

  marker.bindPopup(ownerPlotPopup);

  marker.on("click", () => {
    marker.openPopup();
  });

  marker.on("add", () => {
    const markerElement = marker.getElement();
    if (markerElement) {
      L.DomUtil.addClass(markerElement, "leaflet-marker-icon-color-red");
    }
  });
  return marker;
};
